import { addYears, differenceInMonths, differenceInYears, formatISO, parseISO } from "date-fns";

export function calculateDatesDifference( from, to = null ) {

    if ( !from ) {
        return { years: 0, months: 0 };
    }

    let fromDate;
    let toDate;
    try {
        fromDate = parseISO( from );
        toDate = to ? parseISO( to ) : new Date();
    } catch ( e ) {
        return { years: 0, months: 0 };
    }

    if ( Number.isNaN( fromDate?.getTime?.() ) || Number.isNaN( toDate?.getTime?.() ) ) {
        return { years: 0, months: 0 };
    }

    let years = differenceInYears( toDate, fromDate );

    const startDatePlusYears = addYears( fromDate, years );

    let months = differenceInMonths( toDate, startDatePlusYears )
        + ( toDate.getDate() > 1 ? 1 : 0 );

    // assure que le nombre de mois n'est pas négatif, le mois est réduit à zéro
    if ( months < 0 ) {
        months = 0
    }

    while ( months > 12 ) {
        years += 1;
        months -= 12;
    }

    return {
        years: years,
        months: months,
    };
}

function parseExperienceDate( value ) {
    if ( value instanceof Date ) {
        return Number.isNaN( value.getTime() ) ? null : new Date( value );
    }

    if ( !value ) {
        return null;
    }

    const date = parseISO( value );
    return Number.isNaN( date.getTime() ) ? null : date;
}

function fallbackDurationInMonths( duration ) {
    if ( typeof duration === 'number' && Number.isFinite( duration ) ) {
        return Math.max( 0, Math.floor( duration / 30 ) );
    }

    if ( typeof duration !== 'string' ) {
        return 0;
    }

    const years = Number( duration.match( /(\d+)\s+years?/i )?.[ 1 ] || 0 );
    const months = Number( duration.match( /(\d+)\s+mons?/i )?.[ 1 ] || 0 );
    const days = Number( duration.match( /(\d+)\s+days?/i )?.[ 1 ] || 0 );

    return ( years * 12 ) + months + Math.floor( days / 30 );
}

export function calculateTotalExperienceDuration(
    experiences = [],
    fallbackDuration = 0,
    currentDate = new Date()
) {
    const now = parseExperienceDate( currentDate ) || new Date();

    // Calcule la durée totale des expériences en fusionnant les périodes qui se chevauchent.
    const intervals = experiences
        .map( experience => {
            const from = parseExperienceDate( experience?.from );
            const to = experience?.isCurrent || !experience?.to
                ? now
                : parseExperienceDate( experience?.to );

            if ( !from || !to || to < from ) {
                return null;
            }

            return { from, to };
        } )
        .filter( Boolean )
        .sort( ( first, second ) => first.from - second.from );

    const mergedIntervals = intervals.reduce( ( merged, interval ) => {
        const previous = merged[ merged.length - 1 ];

        if ( previous && interval.from <= previous.to ) {
            if ( interval.to > previous.to ) {
                previous.to = interval.to;
            }
            return merged;
        }

        return [ ...merged, { ...interval } ];
    }, [] );

    const monthsFromDates = mergedIntervals.reduce( ( total, interval ) => {
        const { years, months } = calculateDatesDifference(
            formatISO( interval.from, { representation: 'date' } ),
            formatISO( interval.to, { representation: 'date' } )
        );
        return total + ( years * 12 ) + months;
    }, 0 );

    const totalMonths = monthsFromDates > 0
        ? monthsFromDates
        : fallbackDurationInMonths( fallbackDuration );

    return {
        years: Math.floor( totalMonths / 12 ),
        months: totalMonths % 12,
        totalMonths,
    };
}
